import React from 'react';
import { View, ScrollView, ViewStyle, StyleSheet } from 'react-native';
import { SafeAreaView, useSafeAreaInsets } from 'react-native-safe-area-context';
import { Theme } from '../../utils/theme';

// Page Container - Main page wrapper
interface PageContainerProps {
  children: React.ReactNode;
  safe?: boolean;
  style?: ViewStyle;
}

export function PageContainer({ children, safe = true, style }: PageContainerProps) {
  const Container = safe ? SafeAreaView : View;
  
  return (
    <Container style={[styles.pageContainer, style]}>
      {children}
    </Container>
  );
}

// Content Container - For scrollable content with consistent padding
interface ContentContainerProps {
  children: React.ReactNode;
  scrollable?: boolean;
  style?: ViewStyle;
  showsVerticalScrollIndicator?: boolean;
}

export function ContentContainer({ 
  children, 
  scrollable = true, 
  style,
  showsVerticalScrollIndicator = false 
}: ContentContainerProps) {
  if (scrollable) {
    return (
      <ScrollView 
        style={[styles.scrollContainer, style]}
        contentContainerStyle={styles.contentContainer}
        showsVerticalScrollIndicator={showsVerticalScrollIndicator}
      >
        {children}
      </ScrollView>
    );
  }
  
  return (
    <View style={[styles.contentContainer, style]}>
      {children}
    </View>
  );
}

// Header Container - For page headers with title and subtitle
interface HeaderContainerProps {
  children: React.ReactNode;
  style?: ViewStyle;
}

export function HeaderContainer({ children, style }: HeaderContainerProps) {
  return (
    <View style={[styles.headerContainer, style]}>
      {children}
    </View>
  );
}

// Bottom Container - For bottom buttons and actions
interface BottomContainerProps {
  children: React.ReactNode;
  style?: ViewStyle;
}

export function BottomContainer({ children, style }: BottomContainerProps) {
  const insets = useSafeAreaInsets();
  
  return (
    <View style={[styles.bottomContainer, { paddingBottom: Theme.spacing.xxl + insets.bottom }, style]}>
      {children}
    </View>
  );
}

const styles = StyleSheet.create({
  pageContainer: {
    ...Theme.components.pageContainer,
  },
  scrollContainer: {
    flex: 1,
  },
  contentContainer: {
    ...Theme.components.contentContainer,
  },
  headerContainer: {
    ...Theme.components.headerContainer,
  },
  bottomContainer: {
    padding: Theme.spacing.xxl,
    // paddingBottom is dynamically set in component using useSafeAreaInsets()
  },
}); 